import { useState } from 'react'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { Alert, AlertType } from 'components/Alert/Alert'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import useModalClose from 'hooks/useModalClose'
import { Button, ColorVariant, CursorPointer, Flex, colors } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { IFoodKitchen } from 'types/foodLogistics/foodKitchen.interface'
import { IMealWindow, IPlanAdjustment } from 'types/foodLogistics/mealWindow.interface'
import { FOOD_DIETARY_TAGS } from '../foodData'

interface IAdjustLine {
  kitchenId: number
  tag: string
  qty: string
}

interface IProps {
  window: IMealWindow
  kitchens: IFoodKitchen[]
  manual: number
  addAdjustment: (windowId: number, lines: IPlanAdjustment[], reason: string, locked: boolean) => void
  closeModal: () => void
}

export const AdjustPlanModal = ({ window, kitchens, manual, addAdjustment, closeModal }: IProps) => {
  const [adjLines, setAdjLines] = useState<IAdjustLine[]>([{ kitchenId: kitchens[0]?.id, tag: 'Regular', qty: '' }])
  const [reason, setReason] = useState('')
  useModalClose(closeModal)

  const tagOptions = FOOD_DIETARY_TAGS.map((t) => ({ label: t, value: t }))
  const kitchenOptions = kitchens.map((k) => ({ label: k.name, value: k.id }))

  const dietBad = adjLines.find((l) => {
    const k = kitchens.find((x) => x.id === Number(l.kitchenId))
    return k && !k.caps.includes(l.tag)
  })
  const valid = reason.trim() && adjLines.every((l) => Number(l.qty) > 0) && !dietBad

  const handleSubmit = () => {
    const lines: IPlanAdjustment[] = adjLines.map((l) => ({
      kitchenId: Number(l.kitchenId),
      tag: l.tag,
      qty: Number(l.qty),
      reason,
      routedToStandby: window.locked,
    }))
    addAdjustment(window.id, lines, reason, window.locked)
    closeModal()
  }

  return (
    <Modal black width='560px'>
      <Flex column gap='1rem' width='100%'>
        <Flex between width='100%' center>
          <TitleText weight='bold'>Adjust Plan — {window.name}</TitleText>
          <CursorPointer onClick={closeModal}>
            <CloseIcon />
          </CursorPointer>
        </Flex>
        <OtherText grey>
          Derived plan: {window.planned}
          {manual > 0 && ` · existing manual: +${manual}`}
        </OtherText>
        {window.locked ? (
          <Alert
            title='Cut-off has passed'
            description='Hot orders are locked per kitchen — these quantities will be routed to standby reserve at the selected kitchens instead of increasing hot orders.'
            type={AlertType.WARNING}
            colorVariant={ColorVariant.PRIMARY}
          />
        ) : (
          <Alert
            title='Manual adjustment'
            description='Increases create MANUAL_ADJUSTMENT intent records (per diet, per kitchen). They survive schedule regeneration and are excluded from show-rate learning.'
            type={AlertType.INFO}
            colorVariant={ColorVariant.SECONDARY}
          />
        )}
        <BodyText weight='600'>Additional meals — per kitchen, per dietary tag</BodyText>
        {adjLines.map((l, i) => {
          const k = kitchens.find((x) => x.id === Number(l.kitchenId))
          const incompatible = k && !k.caps.includes(l.tag)
          return (
            <Flex column gap='0.25rem' key={i} width='100%'>
              <Flex gap='0.5rem' width='100%' alignEnd>
                <HonestSelectInput
                  width='100%'
                  options={kitchenOptions}
                  defaultValue={l.kitchenId}
                  onChange={(v: any) => setAdjLines(adjLines.map((x, j) => (j === i ? { ...x, kitchenId: Number(v.value) } : x)))}
                  name='kitchenId'
                  labelText='Kitchen'
                  nomarg
                />
                <HonestSelectInput
                  width='100%'
                  options={tagOptions}
                  defaultValue={l.tag}
                  onChange={(v: any) => setAdjLines(adjLines.map((x, j) => (j === i ? { ...x, tag: v.value } : x)))}
                  name='tag'
                  labelText='Dietary'
                  nomarg
                />
                <Input
                  type='number'
                  placeholder='Quantity'
                  value={l.qty}
                  onChange={(e: any) => setAdjLines(adjLines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))}
                  labelText='Quantity'
                  nomarg
                />
                {adjLines.length > 1 && (
                  <CursorPointer onClick={() => setAdjLines(adjLines.filter((_, j) => j !== i))}>
                    <CloseIcon />
                  </CursorPointer>
                )}
              </Flex>
              {incompatible && (
                <OtherText color={colors.red}>Dietary hard stop: {k!.name} does not cover {l.tag}</OtherText>
              )}
            </Flex>
          )
        })}
        <Button
          orangeBorder
          small
          onClick={() => setAdjLines([...adjLines, { kitchenId: kitchens[0]?.id, tag: 'Regular', qty: '' }])}
        >
          Add line
        </Button>
        <OtherText grey>
          Reducing the plan? Don&apos;t use this — select people in the Employee List and use Cancel Selected.
        </OtherText>
        <Input
          type='textarea'
          labelText='Reason (mandatory — shown on the window card)'
          value={reason}
          onChange={(e: any) => setReason(e.target.value)}
          placeholder='e.g. Client delegation arriving for lunch'
          nomarg
        />
        <Flex gap='0.5rem' justcenter width='100%'>
          <Button onClick={closeModal}>Cancel</Button>
          <Button orange disabled={!valid} onClick={handleSubmit}>
            Submit
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}
